/* eslint-disable multiline-ternary */
/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import styles from './styles';
import { Box, CircularProgress, Dialog, Divider, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { CancelPresentationRounded, ChevronLeft, ChevronRight, ShortcutOutlined } from '@mui/icons-material';
import CanvasJSReact from '../../canvasjs/canvasjs.react';
import Datagrid from '../../Datagrid';
import { columns, customColumns } from './table_config';
import { useDispatch, useSelector } from 'react-redux';
import { downloadDataReport, downloadRolloverPDF, listHydraulicsDetails } from '../../../../store/actions/hydraulics';
import { DISPLACEMENT_TYPE_COLORS, HYDRAULICS_CHART_COLORS } from '../../../../shared/config/hydraulics_constants';
import { listRollover } from '../../../../store/actions/hydraulicsRollover';
import { HYDRAULICS_RUN_TYPES, MAIN_DEPTH_POINTS, REPORT_INPUT_LIST, ROLLOVER_DISPLACEMENT_TYPE_SECONDARY, TOOLTIPS } from '../../../../utils/constants';
import ReportInputsModal from '../ReportInputsModal';
import { getOptimalInterval } from '../../../../utils/getOptimalInterval';
import HydraulicsDetailsModal from '../HydraulicsRunModalDialog';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';
import { getDepthValueFromField } from '../../../../utils/depthSummaryExtractor';
import ReportsDownloadSelector from '../ReportsDownloadSelector';
import VersionText from '../VersionText';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const RolloverChart = ({
  onCloseClick,
  isOpen,
  rowData,
  wellDetails,
  hydraulicWellsDetails
}) => {
  const classes = styles();
  const [tableRow, setTableRows] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const rollover = useSelector((state) => state.rollover);
  const dispatch = useDispatch();
  const hydraulics = useSelector((state) => state.hydraulics);
  const [loader, setLoader] = useState(false);
  const [customColumn, setCustomColumn] = useState({
    header: null,
    field: null,
    show: true
  });
  const [titleHeader, setTitleHeader] = useState('Rollover');
  const [plotForPrimary, setPlotForPrimary] = useState('Pressure');
  const [plotForSecondary, setPlotForSecondary] = useState('Hole Depth');
  const [secondaryDepthField, setSecondaryDepthField] = useState([]);
  const [showReportInput, setShowReportInputs] = useState(false);
  const [tableList, setTableList] = useState(REPORT_INPUT_LIST.map(item => item.key));
  const [reportPreview, setReportPreview] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [chartOptions, setChartOptions] = useState({});
  const [showHydraulicsParameterDialog, setShowHydraulicsParameterDialog] = useState({
    open: false,
    data: {},
    dialogParameter: ''
  });
  const sbpField = 'connection_sbp';

  useEffect(() => {
    if (loader || hydraulics?.loading) {
      document.body.style.cursor = 'wait';
    } else {
      document.body.style.cursor = 'default';
    }
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [loader, hydraulics]);

  useEffect(() => {
    const body = {
      runId: rowData?._id,
      wellsInfoId: rowData?.wellsInfoId
    };
    if (rowData?.isRolloverResults) {
      dispatch(listRollover(body));
    }
  }, [rowData]);

  useEffect(() => {
    if (!rollover?.loading) {
      const rolloverData = rollover?.data?.[0];
      if (!rolloverData?.results?.length) {
        setTableRows([]);
        return;
      }

      const targetData = rolloverData.run_details_copy?.target_data;
      const anchorPoint = targetData?.anchor_point;
      const isTargetDataEmpty = !targetData || Object.keys(targetData).length === 0 || targetData?.anchor_point === 'None';

      if (isTargetDataEmpty) {
        setCustomColumn({ header: null, show: true, field: null });
        setTitleHeader('Rollover');
      } else {
        const { decimals = 2, label = '' } = rollover?.units?.target_data?.target_ecd ?? {};
        const value = targetData.target_ecd?.toFixed(decimals);
        const headerName = anchorPoint.replace(/_/g, ' ').toUpperCase();
        const fieldName = anchorPoint.replace(/ /g, '_')?.toLowerCase();
        const isExcluded = MAIN_DEPTH_POINTS.includes(anchorPoint);
        setCustomColumn(
          isExcluded
            ? { header: null, show: true, field: null }
            : { header: headerName, show: false, field: `${fieldName}` }
        );

        setTitleHeader(`Rollover (${value} ${label} @ ${headerName})`);
      }

      const modifiedRecords = rolloverData.results.map((record) => ({
        ...record,
        _id: Math.random().toString(36).substring(2, 10)
      }));

      setTableRows(modifiedRecords);
      setShowTable(true);
      if (plotForPrimary === 'Pressure') {
        setChartData(rollover?.data[0]?.results);
      } else {
        const selected = secondaryDepthField.find(
          (f) => f.label === plotForSecondary
        );
        if (selected?.field?.length) {
          const [esdKey, ecdKey] = selected.field;
          const esdData = [];
          const ecdData = [];
          rolloverData.results.forEach((r) => {
            const strokes = r?.strokes ?? 0;

            const esdValue = getDepthValueFromField(r, esdKey) ?? 0;
            const ecdValue = getDepthValueFromField(r, ecdKey) ?? 0;

            esdData.push({
              x: strokes,
              y: esdValue,
            });

            ecdData.push({
              x: strokes,
              y: ecdValue,
            });
          });
          setChartData([
            { name: `ESD @ ${plotForSecondary} (${rollover?.units?.bh_esd?.label})`, data: esdData },
            { name: `ECD @ ${plotForSecondary} (${rollover?.units?.bh_esd?.label})`, data: ecdData }
          ]);
        }
      }
    } else {
      setShowTable(false);
    }
  }, [rollover?.data, rollover?.loading, plotForSecondary, plotForPrimary]);

  const secondaryField = secondaryDepthField.find(
    (type) => type.label === plotForSecondary
  );

  useEffect(() => {
    const secondaryFields = rollover?.data?.[0]?.secondary_fields;
    setSecondaryDepthField(secondaryFields || ROLLOVER_DISPLACEMENT_TYPE_SECONDARY);
  }, [rollover?.data]);

  const generateViewportMinMax = () => {
    const allDepthPoints = chartData.flatMap(item => item.data ?? []);
    const minMaxData = [...allDepthPoints];
    const keysToCheck = ['y'];

    const allValues = [];

    // Collect all values from the specified keys
    keysToCheck.forEach(key => {
      minMaxData.forEach(obj => {
        if (key in obj) {
          allValues.push(obj[key]);
        }
      });
    });

    // Calculate the overall lowest and highest values
    const overallLowest = Math.min(...allValues);
    const overallHighest = Math.max(...allValues);

    const results = {
      lowest: overallLowest,
      highest: overallHighest,
    };
    return results;
  };

  useEffect(() => {
    if (!chartData?.length) return;
    const options =
      plotForPrimary === 'Pressure' ? {
        height: 900,
        animationEnabled: true,
        theme: 'light2',
        backgroundColor: '#FFF',
        legend: {
          fontSize: 18,
        },
        axisY: {
          title: 'Strokes',
          includeZero: true,
          reversed: true,
          titleFontSize: 22,
          labelFontSize: 16,
          gridThickness: 1,
        },
        axisX: {
          title: `Pressure (${rollover?.units?.sbp?.label})`,
          minimum: 0,
          titleFontSize: 22,
          labelFontSize: 16,
          gridThickness: 1,
        },
        axisX2: {
          title: `Flow Rate (${rollover?.units?.flow_rate?.label})`,
          titleFontSize: 22,
          labelFontSize: 16,
          minimum: 0,
          gridThickness: 1,
          labelPlacement: 'outside',
          labelFormatter: function (e) {
            return Math.round(e.value);
          },
        },
        data: [
          {
            type: 'line',
            name: `Dynamic SBP (${rollover?.units?.sbp?.label})`,
            axisXType: 'primary',
            showInLegend: true,
            lineThickness: 1,
            color: HYDRAULICS_CHART_COLORS.DISPLACEMENT_SBP,
            toolTipContent: `Strokes: {y} <br/> Dynamic SBP: {x} ${rollover?.units?.sbp?.label}`,
            dataPoints: rollover?.data[0]?.results?.map((record) => ({
              x: record.sbp,
              y: record.strokes,
            })) || [],
          },
          {
            type: 'line',
            name: `Connection SBP (${rollover?.units?.connection_sbp?.label})`,
            axisXType: 'primary',
            showInLegend: true,
            lineThickness: 1,
            color: HYDRAULICS_CHART_COLORS.DISPLACEMENT_CONNECTION_SBP,
            toolTipContent: `Strokes: {y} <br/> Connection SBP: {x} ${rollover?.units?.connection_sbp?.label}`,
            dataPoints: rollover?.data[0]?.results?.map((record) => ({
              x: record.connection_sbp,
              y: record.strokes,
            })) || [],
          },
          {
            type: 'line',
            axisXType: 'secondary',
            name: `Flow Rate (${rollover?.units?.flow_rate?.label})`,
            showInLegend: true,
            lineThickness: 1,
            color: HYDRAULICS_CHART_COLORS.DISPLACEMENT_FLOW_RATE,
            toolTipContent: `Strokes: {y} <br/>Flow Rate: {x} ${rollover?.units?.flow_rate?.label}`,
            dataPoints: rollover?.data[0]?.results?.map((record) => ({
              x: record.flow_rate,
              y: record.strokes,
            })) || [],
          },
        ],
      }
        : {
            height: 900,
            animationEnabled: true,
            theme: 'light2',
            backgroundColor: '#FFF',
            legend: {
              fontSize: 18,
              verticalAlign: 'top',
              horizontalAlign: 'center',
            },
            axisY: {
              title: 'Strokes',
              includeZero: true,
              reversed: true,
              titleFontSize: 22,
              labelFontSize: 16,
              gridThickness: 1,
            },
            axisX: {
              title: `EMW @ ${plotForSecondary} (${rollover?.units?.bh_ecd?.label ?? ''})`,
              viewportMinimum: generateViewportMinMax()?.lowest - 0.1,
              viewportMaximum: generateViewportMinMax()?.highest + 0.1,
              interval: getOptimalInterval(generateViewportMinMax()?.lowest, generateViewportMinMax()?.highest, 10),
              titleFontSize: 22,
              labelFontSize: 16,
              gridThickness: 1,
            },
            axisX2: {
              title: `Flow Rate (${rollover?.units?.flow_rate?.label})`,
              titleFontSize: 22,
              labelFontSize: 16,
              minimum: 0,
              gridThickness: 1,
              labelPlacement: 'outside',
              labelFormatter: function (e) {
                return Math.round(e.value);
              },
            },
            data: [
              ...(Array.isArray(chartData) && chartData.length > 0
                ? chartData.map((dataset, i) => ({
                  type: 'line',
                  name: dataset?.name ?? `Series ${i + 1}`,
                  lineThickness: 2,
                  showInLegend: true,
                  color:
                  i === 0
                    ? DISPLACEMENT_TYPE_COLORS.ESD
                    : DISPLACEMENT_TYPE_COLORS.ECD,
                  toolTipContent: `Strokes: {y} <br/> ${dataset?.name ?? ''}: {x} ${rollover?.units?.bh_ecd?.label ?? ''
                  }`,
                  dataPoints:
                  dataset?.data?.map?.((d) => ({
                    x: d?.y ?? 0, // EMW value
                    y: d?.x ?? 0, // Strokes
                  })) ?? [],
                }))
                : []),
              {
                type: 'line',
                axisXType: 'secondary',
                name: `Flow Rate (${rollover?.units?.flow_rate?.label})`,
                showInLegend: true,
                lineThickness: 1,
                color: HYDRAULICS_CHART_COLORS.DISPLACEMENT_FLOW_RATE,
                toolTipContent: `Strokes: {y} <br/> Flow Rate: {x} ${rollover?.units?.flow_rate?.label}`,
                dataPoints: rollover?.data?.[0]?.results?.map((record) => ({
                  x: record.flow_rate ?? 0,
                  y: record.strokes ?? 0,
                })) || [],
              },
            ],
          };
    setChartOptions(options);
  }, [chartData, plotForPrimary, plotForSecondary, rollover]);

  const handlePDFReportDownload = () => {
    const body = {
      inputs: tableList,
      jobId: rowData?.jobId,
      wellsInfoId: rowData?.wellsInfoId,
      runId: rowData?._id,
      wellName: wellDetails?.Well,
      rigName: wellDetails?.Rig,
      runName: rowData?.name,
      type: plotForPrimary,
      ...(plotForPrimary !== 'Pressure' && { depth: plotForSecondary }),
    };
    setLoader(true);
    dispatch(downloadRolloverPDF(body, reportPreview, () => {
      setLoader(false);
    }));
  };

  const handleDataReportDownload = () => {
    setLoader(true);
    const body = {
      jobId: rowData?.jobId,
      wellsInfoId: rowData?.wellsInfoId,
      runId: rowData?._id,
      wellName: wellDetails?.Well,
      rigName: wellDetails?.Rig,
      runName: rowData?.name,
      type: HYDRAULICS_RUN_TYPES?.ROLLOVER?.typeKey,
    };
    dispatch(downloadDataReport(body, () => {
      setLoader(false);
    }));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onCloseClick();
      }}
      maxWidth={'lg'}
      fullWidth={true}
      style={{
        marginRight: '20px'
      }}
      PaperProps={{
        sx: {
          overflow: 'visible'
        },
      }}
      className={classes.dialogModal}
    >
      {rollover?.loading || hydraulics?.loading
        ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ height: '700px' }}
          >
            <CircularProgress />
          </Box>
          )
        : (
          <>
      <Box className={classes.dialogTitle}>
        <Typography className={classes.titleHeader}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              {`${wellDetails?.Rig} | ${wellDetails?.Well} | ${rowData?.name}`}
            </Grid>
            <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
              {rowData?.isRolloverResults && rollover?.data[0]?.results?.length ? (
                <ReportsDownloadSelector
                  loading={loader || hydraulics.downloadRolloverPDF === 'loading' || hydraulics?.loading}
                  onDataDownload={handleDataReportDownload}
                  onPreviewOpen={(previewItem) => {
                    setShowReportInputs(true);
                    setReportPreview(previewItem);
                  }}
                />
              ) : null
              }
              <Tooltip title={TOOLTIPS.viewRunInputs}>
                <span>
                  <IconButton
                    disabled={hydraulics.downloadRolloverPDF === 'loading' || hydraulics?.loading}
                    onClick={() => {
                      dispatch(listHydraulicsDetails(rowData));
                      setShowHydraulicsParameterDialog({
                        open: true,
                        data: rowData,
                        dialogParameter: 'rollover'
                      });
                    }}>
                    <ShortcutOutlined fontSize='large' />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                onClick={() => {
                  onCloseClick();
                }}
              >
                <CancelPresentationRounded fontSize='large' />
              </IconButton>
            </Grid>
          </Grid>
        </Typography>
      </Box>
      <Divider />
      {rowData?.isRolloverResults ? <>
      <Grid container item xs={showTable ? 7 : 12} style={{ marginTop: showTable ? '1.25%' : '2%' }} alignItems="center" justifyContent={'center'} >
        {!rollover.loading && <>
          <Box display="flex" justifyContent="center" alignItems="center">
            <Grid item className={classes.titleHeader}>
              {titleHeader}
            </Grid>
          </Box></>}
      </Grid>
      <Box className={classes.boxStyle}>
        <Grid container>
          <Grid item xs={showTable ? 7 : 12} sx={{
            p: 2,
            pt: 10,
            '.canvasjs-chart-container canvas': {
              cursor: loader || hydraulics?.loading ? 'wait !important' : ''
            }
          }}>
            {rollover.loading
              ? (
                <Box
                  className={classes.progressBarStyle}
                >
                  <CircularProgress />
                </Box>
                )
              : chartOptions
                ? (
                  <CanvasJSChart options={chartOptions} />
                  ) : (
                  <Typography variant="body2" color="textSecondary">
                    No data available to display.
                  </Typography>
                  )}
          </Grid>
          {!showTable && <Box className={classes.menuTogglerBtn}>
            <IconButton
              onClick={() => {
                setShowTable(!showTable);
              }}>
              <ChevronLeft className={classes.iconSize} />
            </IconButton>
          </Box>}
          {showTable && <Grid item xs={5} marginTop={'2.5%'}>
            <Box className={classes.menuTogglerTableBtn}>
              <IconButton
                onClick={() => {
                  setShowTable(!showTable);
                }}>
                <ChevronRight className={classes.iconSize} />
              </IconButton>
            </Box>
            <Box display="flex" justifyContent="flex-start" marginLeft={'2%'}>
              <Grid item xs={3}>
                <Box
                  display="flex"
                  justifyContent="flex-start"
                  alignItems="center"
                  width="400px"
                  gap={2}
                >
                  {/* Type Dropdown */}
                  <Grid item>
                    <FormControl
                      fullWidth
                      sx={{ bgcolor: 'transparent', width: '160px' }}
                    >
                      <InputLabel id="filter-input-label">Type</InputLabel>
                      <Select
                        labelId="filter-select-id"
                        id="filter-id"
                        value={plotForPrimary}
                        label="Filter"
                        disabled={rollover?.loading}
                        onChange={(e) => setPlotForPrimary(e.target.value)}
                        sx={{
                          fontWeight: 'bolder',
                          fontSize: '18px',
                          background: 'unset',
                        }}
                      >
                        {['Pressure', 'EMW'].map((item, idItem) => (
                          <MenuItem
                            key={`item_${item}_${idItem}`}
                            value={item}
                            sx={{ fontSize: '18px', fontWeight: 'bold' }}
                          >
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Show secondary depth filter only when EMW is selected */}
                  {plotForPrimary === 'EMW' && (
                    <>
                      <Grid item>
                        <Typography sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                          at
                        </Typography>
                      </Grid>

                      <Grid item>
                        <FormControl
                          fullWidth
                          sx={{ bgcolor: 'transparent', width: '200px' }}
                        >
                          <InputLabel id="depth-input-label">Depth</InputLabel>
                          <Select
                            labelId="depth-select-id"
                            id="depth-id"
                            value={plotForSecondary}
                            label="Depth"
                            disabled={rollover?.loading}
                            onChange={(e) => setPlotForSecondary(e.target.value)}
                            sx={{
                              fontWeight: 'bolder',
                              fontSize: '18px',
                              background: 'unset',
                            }}
                          >
                            {secondaryDepthField.map((item, idItem) => (
                              <MenuItem
                                key={`item_${item}_${idItem}`}
                                value={item.label}
                                sx={{
                                  color: item?.color,
                                  fontSize: '18px',
                                  fontWeight: 'bold',
                                }}
                              >
                                {item.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                </Box>
              </Grid>
            </Box>
            {showTable && <Grid item xs={12}>
              <Datagrid
                fileName={`${wellDetails?.Well?.trim()}-${rowData?.name?.trim()}-Mud-Displacement`}
                loading={rollover.loading}
                data={tableRow}
                sx={{
                  '.MuiDataGrid-cell': {
                    fontSize: '1rem',
                    padding: '2px',
                  },
                  '.MuiDataGrid-columnHeader': {
                    fontSize: '1.2rem',
                    fontWeight: '900 !important',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    overflow: 'visible',
                    lineHeight: '1.43rem',
                    whiteSpace: 'normal',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    minHeight: '0px'
                  },
                }}
                columns={plotForPrimary === 'Pressure' ? columns(rollover?.units, customColumn, 'ECD', sbpField) : customColumns(rollover?.units, secondaryField, sbpField)}
                disableStickyHeader={true}
                autoHeight={false}
                hideFooterPagination
                hideFooterSelectedRowCount
                height={'900px'}
                getRowHeight={() => 'auto'}
                pageSize={100}
                toolbar={{
                  options: {
                    columns: false,
                    filters: false,
                    density: false,
                    export: {
                      enabled: true,
                    }
                  },
                }}
              />
            </Grid>}
          </Grid>}
        </Grid>
      </Box>
        </> : <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '50vh',
                  textAlign: 'center',
                  p: 4,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}
                >
                  No Results Found
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.disabled' }}
                >
                  Try adjusting parameters to generate results.
                </Typography>
              </Box>
            </>}
            </>)}
      {
        showReportInput
          ? <ReportInputsModal
            isOpen={showReportInput}
            tableList={tableList}
            setTableList={setTableList}
            onCloseClick={() => {
              setShowReportInputs(false);
            }}
            generateReport={handlePDFReportDownload}
          />
          : null
      }
      {showHydraulicsParameterDialog.open && showHydraulicsParameterDialog?.data?.name
        ? (
          <HydraulicsDetailsModal
            selectedJob={wellDetails}
            isOpen={showHydraulicsParameterDialog.open}
            onCloseClick={() => {
              setShowHydraulicsParameterDialog({
                open: false,
                data: {}
              });
              dispatch({
                type: REDUX_ACTIONS.HYDRAULICS_SET_ID,
                payload: {
                  data: '',
                },
              });
            }}
            data={showHydraulicsParameterDialog.data}
            dialogParameter={showHydraulicsParameterDialog.dialogParameter}
            wellDetails={hydraulicWellsDetails}
          />
          )
        : null}
        <VersionText version={rollover?.ph_version} loading={rollover.loading} />
    </Dialog>
  );
};
RolloverChart.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  rowData: PropTypes.any,
  wellDetails: PropTypes.any,
  hydraulicWellsDetails: PropTypes.any
};
RolloverChart.defaultProps = {
  selectedJob: {}
};

export default RolloverChart;
